import styled from 'styled-components'
import { observer } from 'mobx-react-lite'
import {
	ProductList,
	ProductListItemClickHandle,
	ProductListItemData,
	ProductListItemOnPriceChange,
} from './ProductList'
import React from 'react'
import {
	ParseState,
	ProductTypeSearchResultHeader,
	SortState,
} from './ProductTypeSearchResultHeader'
import { ProductCurrentFilterTag } from '@/modules/products/components/ProductCurrentFilterTag'
import { FilterItemType, FilterMap } from '@/modules/products/viewModels/ProductListViewModel'
import { Menu } from 'antd'
import { ActiveLink } from '@/components/ActiveLink'
import {
	CATEGORY_HANDOUT_PAGE,
	CATEGORY_TEXTBOOK_PAGE,
	CATEGORY_WORKBOOK_PAGE,
} from '@/settings/constant'
import { themeGet } from '@styled-system/theme-get'
import useWindowDimensions from '@/lib/web-ui/useWindowDimensions'
import { useRouter } from 'next/router'
import { includesPath } from '@/lib/utils/url'

export interface ProductUnifiedSearchResultProps {
	// textBooks: ProductListItemData[]
	// handouts: ProductListItemData[]
	// workbooks: ProductListItemData[]
	dataSource: ProductListItemData[]
	onItemClick: ProductListItemClickHandle
	onPriceChange: ProductListItemOnPriceChange
	onEmptyClick: () => void
	onFilterClick: () => void
	filterItems: FilterMap
	deleteFilterItem: (item: FilterItemType) => void
	sorting: (sort: SortState) => void
	parsing?: (parse: ParseState) => void
	parsingState?: ParseState
	onSelectAll: (checked: boolean) => void
	allChecked: boolean
	selectedItems: ProductListItemData[]
	onChangeSelectedItems: (checked: boolean, item: ProductListItemData) => void
	sortStatus: SortState
	totalCount: number
	category?: string
	useParsingBadge: boolean
}

export const ProductUnifiedSearchResultWrapper = styled.div`
	@media (max-width: 1200px) {
		min-height: 60vh;
	}

	.ant-menu {
		display: none;

		.ant-menu-item {
			flex: unset;
			padding: 0;
			text-align: center;

			&:nth-of-type(1) {
				width: 15%;
				min-width: 70px;
			}
			&:nth-of-type(2),
			&:nth-of-type(3) {
				flex: 1;
			}
		}

		.active-link {
			height: auto;
		}

		@media (max-width: 1200px) {
			display: block;

			.ant-menu-item {
				margin: 0;

				&:after {
					display: none;
				}
				&:nth-of-type(3),
				&:nth-of-type(4) {
					width: 140px;
				}

				span {
					a {
						color: ${themeGet('text.3', '#BBBBBF')};
					}
				}

				&.ant-menu-item-selected {
					span {
						position: relative;
						padding: 0 8px;

						&:before {
							display: block;
							content: '';
							position: absolute;
							bottom: -15px;
							left: 0;
							right: 0;
							width: 100%;
							height: 2px;
							background: ${themeGet('border.3', '#1B1B29')};
						}

						a {
							font-weight: bold;
							color: ${themeGet('text.0', '#1B1B29')};
						}
					}
				}
			}
		}
		@media (max-width: 768px) {
			display: flex;
			padding: 0 10px;

			.ant-menu-item {
				&:nth-of-type(1) {
					width: 20%;
				}
				&:nth-of-type(2) {
					width: 20%;
				}
				&:nth-of-type(3),
				&:nth-of-type(4) {
					min-width: 100px;
				}

				&.ant-menu-item-selected {
					span {
						padding: 0 5px;
					}
				}
			}
		}
	}

	> .section {
		padding-top: 32px;
		padding-bottom: 16px;

		&:nth-of-type(1) {
			padding-top: 24px;

			@media (max-width: 768px) {
				padding-top: 0;
			}
		}
		@media (max-width: 1200px) {
			padding-top: 8px;
		}
		@media (max-width: 768px) {
			padding-top: 0;
		}
	}
`

const mobileMenuFilters: { label: string; value: string; path: string }[] = [
	{
		label: '수업자료',
		value: '1',
		path: CATEGORY_HANDOUT_PAGE,
	},
	{
		label: '교과서',
		value: '2',
		path: CATEGORY_TEXTBOOK_PAGE,
	},
	{
		label: '참고서',
		value: '3',
		path: CATEGORY_WORKBOOK_PAGE,
	},
]

export const ProductTypeSearchResult = observer<ProductUnifiedSearchResultProps>(
	({
		dataSource,
		onItemClick,
		filterItems,
		onEmptyClick,
		onFilterClick,
		sorting,
		parsing,
		parsingState,
		onPriceChange,
		deleteFilterItem,
		onSelectAll,
		allChecked,
		selectedItems,
		onChangeSelectedItems,
		sortStatus,
		totalCount,
		category,
		useParsingBadge,
	}) => {
		const router = useRouter()
		const { asPath } = router
		const { width } = useWindowDimensions()

		return (
			<ProductUnifiedSearchResultWrapper>
				{/* [모바일용] 수업자료/교과서라이선스/참고서라이선스 메뉴 버튼 */}
				<Menu mode={'horizontal'}>
					{mobileMenuFilters &&
						mobileMenuFilters.map((item, idx) => (
							<Menu.Item key={`mobile_filter_${idx}`}>
								<ActiveLink
									className={
										includesPath(asPath, item.path)
											? 'active-link active'
											: 'active-link'
									}
									href={`${item.path}`}>
									{item.label}
								</ActiveLink>
							</Menu.Item>
						))}
				</Menu>

				<div className="section">
					{Object.keys(filterItems).length > 0 && width > 1200 && (
						<ProductCurrentFilterTag
							onFilterClick={onFilterClick}
							filterItems={filterItems}
							deleteFilterItem={deleteFilterItem}
						/>
					)}

					{width <= 1200 && (
						<ProductCurrentFilterTag
							onFilterClick={onFilterClick}
							filterItems={filterItems}
							deleteFilterItem={deleteFilterItem}
						/>
					)}

					<ProductTypeSearchResultHeader
						sorting={sorting}
						parsing={parsing}
						parsingState={parsingState}
						onEmptyClick={onEmptyClick}
						onItemClick={onItemClick}
						onSelectAll={onSelectAll}
						sortStatus={sortStatus}
						totalCount={totalCount}
						category={category}
						useParsingBadge={useParsingBadge}
					/>
					<ProductList
						onPriceChange={onPriceChange}
						onClick={onItemClick}
						dataSource={dataSource}
						onEmptyClick={onEmptyClick}
						allChecked={allChecked}
						selectedItems={selectedItems}
						onChangeSelectedItems={onChangeSelectedItems}
					/>
				</div>
			</ProductUnifiedSearchResultWrapper>
		)
	},
)
